import { uploadUserFile } from '@ps/functions/firebase/uploadUserFile';

import {
  type ChatMessage,
  ChatMessageType,
  type ClientChatMessage,
  type ImageContent,
  type VideoContent,
} from '../types/ChatMessage';

/**
 * Resolve the WIRE content for a message about to be sent. A Picture /
 * Video whose content still carries a locally-picked `File` is uploaded
 * to Firebase Storage first and rewritten to a remote-URL content;
 * every other message passes through unchanged.
 *
 * 1:1 with PopshopRN's `prepareMessageContent`. The optimistic bubble
 * keeps rendering the local blob (it was emitted before this runs); the
 * gateway echo swaps it for the server message once the send lands.
 */
export async function prepareMessageContent(
  message: ClientChatMessage,
): Promise<ChatMessage['content']> {
  switch (message.type) {
    case ChatMessageType.Picture:
      return uploadImageContent(message.content);
    case ChatMessageType.Video:
      return uploadVideoContent(message.content);
    default:
      return message.content;
  }
}

async function uploadImageContent(
  content: ImageContent,
): Promise<ImageContent> {
  if (!('localFile' in content) || !content.localFile) return content;
  const url = await uploadUserFile(content.localFile, {
    metadata: { contentType: content.localFile.type },
  });
  return { width: content.width, height: content.height, imageUrl: url };
}

async function uploadVideoContent(
  content: VideoContent,
): Promise<VideoContent> {
  if (!('localFile' in content) || !content.localFile) return content;
  const url = await uploadUserFile(content.localFile, {
    metadata: { contentType: content.localFile.type },
  });
  // Thumbnail generation is deferred (upstream also sends none); the
  // video bubble renders a first-frame poster when it's absent.
  return { width: content.width, height: content.height, videoUrl: url };
}
